import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AccountViewModel,
  ACCOUNT_STATUS_TYPES,
  ACCOUNT_TEMPLATE_TYPES,
  IAccountResponse,
} from '@models/account.class';
import { IContract, IContractAccountsCollection } from '@models/contract.class';
import { PERMISSION_STATES } from '@models/permission.class';
import { TRANSFER_STATUS_TYPES } from '@models/transfer.class';
import { CashLayers, WithdrawalAmountResponse } from '@models/withdrawal.class';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
} from 'rxjs';
import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  merge,
  skip,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  AppState,
  CASH_LAYERS,
  GEOLOCATION_NOTIFICATION_MESSAGE,
} from 'src/app';
import { selectIsUsa } from 'src/app/state/selectors/current-contract.selectors';
import { selectFeatureFlag } from 'src/app/state/selectors/feature-flag.selectors';
import { SideModalComponent } from 'src/components/side-modal/side-modal.component';
import {
  GeolocationService,
  NotificationService,
  OperationService,
} from 'src/core';

enum BALANCE_AVAILABLE_ACTIONS {
  'transfer' = 'transfer',
  'withdrawal' = 'withdrawal',
  'deposit' = 'deposit',
}

@Component({
  selector: 'app-balance-actions',
  templateUrl: './balance-actions.component.html',
  styleUrls: ['./balance-actions.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BalanceActionsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('sideModal', { static: true }) sideModal: SideModalComponent;
  @Input() isGlobalContract: boolean;
  @Input() currentContract: IContract;
  @Input() isLoremAccount: boolean;
  @Input() userHasDashboard: boolean;
  @Output() onBalanceActionSelected: EventEmitter<boolean> = new EventEmitter();

  public balanceAction: BALANCE_AVAILABLE_ACTIONS;
  public showDepositAccount = false;
  public showTransferButton = false;
  public canTransfer$: ReplaySubject<boolean> = new ReplaySubject(1);
  public transferAccounts$: Observable<AccountViewModel[]>;
  public selectedLoremAccount$: Observable<IContract | AccountViewModel>;
  public isVisibleDepositAccount: boolean;
  public disabledTradingUsaOperation: boolean;

  public isTransferButtonLoading = false;
  public isWithdrawalButtonLoading = false;

  public shouldRestrictBalanceActions$: BehaviorSubject<boolean> =
    new BehaviorSubject(undefined);
  public isUsaContract: boolean;
  public getContractCashLayers$: Observable<CashLayers>;

  private geolocationSubscription: Subscription;

  private contractAccountsCollection: IContractAccountsCollection;
  private currentContract$ = new ReplaySubject<IContract>(1);
  private contractWithAccountsSubscription: Subscription;
  private tradingTransferFeatureFlag: boolean;

  private destroyed$: Subject<void> = new Subject<void>();

  constructor(
    private operationService: OperationService,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private geolocationService: GeolocationService,
  ) {}

  ngOnInit() {
    this.generateAvailableAccounts();
    this.scanForTradingTransfers();
    this.geolocationSubscription =
      this.geolocationService.shouldRestricGeolocationRelatedOperations$.subscribe(
        this.shouldRestrictBalanceActions$,
      );
    this.scanForTradingUsaOperation();
    this.initContractLayersObservable();
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    const { currentContract, isGlobalContract, isLoremAccount } = changes;
    if (
      currentContract &&
      currentContract.currentValue &&
      !isEqual(currentContract.currentValue, currentContract.previousValue)
    ) {
      this.currentContract$.next(currentContract.currentValue);
    }

    if (
      (isGlobalContract &&
        isGlobalContract.currentValue !== isGlobalContract.previousValue) ||
      (isLoremAccount &&
        isLoremAccount.currentValue !== isLoremAccount.previousValue)
    ) {
      this.canShowTransferButton();
    }
  }

  ngOnDestroy() {
    this.contractWithAccountsSubscription &&
      this.contractWithAccountsSubscription.unsubscribe();

    this.geolocationSubscription && this.geolocationSubscription.unsubscribe();

    this.destroyed$.next();
  }

  public closeBalanceAction() {
    this.balanceAction = undefined;
    this.onBalanceActionSelected.next(false);
    this.sideModal.handleHide();
  }

  public toggleOpenDepositAccount() {
    this.showDepositAccount = !this.showDepositAccount;
  }

  public openWithdrawalAction() {
    this.isWithinCashOperationTime$(
      // TODO: Move this to config-level variables
      { hours: 8, minutes: 30 },
      { hours: 13, minutes: 30 },
    ).subscribe((isWithinCashOperationTime) =>
      isWithinCashOperationTime
        ? this.openBalanceAction(BALANCE_AVAILABLE_ACTIONS.withdrawal)
        : this.notificationService.showInfo({
            message:
              'Retiro fuera de horario: Recuerda que el horario para realizar retiros es de 8:30 hrs a 13:30 hrs todos los días hábiles',
          }),
    );
  }

  public openTransferAction() {
    const selectedAccount =
      this.contractAccountsCollection.accounts[this.currentContract.contractID];
    this.operationService
      .getTransfers(selectedAccount.contractId, selectedAccount.accountId)
      .subscribe(
        (transferList) => {
          const pendingTransfer = transferList.items.find(
            (transfer) =>
              transfer.status === TRANSFER_STATUS_TYPES.pending ||
              transfer.status === TRANSFER_STATUS_TYPES.executing,
          );

          if (pendingTransfer) {
            return this.notificationService.showInfo({
              title: 'Transferencia Pendiente',
              message:
                'Tienes una transferencia pendiente, cuando se realice podrás hacer otra transferencia.',
              eventId: undefined,
              isBusinessError: false,
            });
          }

          this.openBalanceAction(BALANCE_AVAILABLE_ACTIONS.transfer);
        },
        () => {
          this.notificationService.showError({
            title: 'Validación de Transferencias',
            message:
              'Ocurrió un error al consultar las transferencias. Intente más tarde.',
            eventId: undefined,
            isBusinessError: false,
          });
        },
      );
  }

  public openBalanceAction(action: BALANCE_AVAILABLE_ACTIONS) {
    const runAction = () => {
      this.balanceAction = action;
      this.onBalanceActionSelected.next(true);
      // Only 'transfer' require the sideModal to be shown, to prevent
      // to have two sideModals open ('withdrawal' has side-modal inside the component)
      action === 'transfer' && this.sideModal.showModal();
    };

    if (!this.shouldRestrictBalanceActions$.value) {
      runAction();
      return;
    }

    /**
     * When the balance actions should be restricted, which happens when
     *   1. The permissionsState are 'prompt' or 'denied', so we notify
     *      the user that they have to accept them.
     *   2. The permissionState is 'granted' and the position hasn't been
     *      obtained yet, so we display the loading state for the buttons.
     */

    if (
      this.geolocationService.permissionsState$.value ===
      PERMISSION_STATES.granted
    ) {
      /**
       * "Waiting" path
       *
       * We add a loading state for the corresponding button, and
       * subscribe to changes in the shouldRestrictBalanceActions$ subject,
       * so when it turns true, we execute the balance action.
       */

      this.toggleButtonLoader(action);

      this.shouldRestrictBalanceActions$
        .pipe(
          skip(1), // Ignore the initial value, since it's a BehaviorSubject
          first(), // Complete the subscription after first emission
        )
        .subscribe(() => {
          runAction();
          this.toggleButtonLoader(action);
        });
      return;
    }

    this.notificationService.showInfo({
      message: GEOLOCATION_NOTIFICATION_MESSAGE,
    });
  }

  private toggleButtonLoader(action: BALANCE_AVAILABLE_ACTIONS) {
    action === BALANCE_AVAILABLE_ACTIONS.transfer
      ? (this.isTransferButtonLoading = !this.isTransferButtonLoading)
      : (this.isWithdrawalButtonLoading = !this.isWithdrawalButtonLoading);
  }

  private canShowTransferButton() {
    this.showTransferButton =
      this.tradingTransferFeatureFlag &&
      this.isLoremAccount &&
      this.userHasDashboard;
  }

  private scanForTradingTransfers() {
    this.store
      .select(selectFeatureFlag('tradingTransfer'))
      .pipe(
        filter(
          (tradingTransfer: any) =>
            tradingTransfer !== undefined &&
            this.tradingTransferFeatureFlag !== tradingTransfer,
        ),
      )
      .subscribe((tradingTransfer: any) => {
        this.tradingTransferFeatureFlag = tradingTransfer;
        this.canShowTransferButton();
      });
  }

  private chekCanTransfer(legacyContractId: string) {
    if (legacyContractId && this.contractAccountsCollection) {
      const selectedAccount =
        this.contractAccountsCollection.accounts[legacyContractId];
      const hasEnoughAccounts =
        selectedAccount &&
        this.getFilteredTransferAccounts(legacyContractId).length > 1;
      return (
        hasEnoughAccounts &&
        (selectedAccount.managementTypeTemplate ===
          ACCOUNT_TEMPLATE_TYPES.TRADING ||
          selectedAccount.managementTypeTemplate ===
            ACCOUNT_TEMPLATE_TYPES.TRADING_USA) &&
        selectedAccount.status === ACCOUNT_STATUS_TYPES.ACTIVE &&
        !this.disabledTradingUsaOperation
      );
    }
    return false;
  }

  private getFilteredTransferAccounts(
    legacyContractId?: string,
  ): AccountViewModel[] {
    const selectedAccount =
      this.currentContract &&
      this.contractAccountsCollection.accounts[
        legacyContractId || this.currentContract.contractID
      ];

    if (selectedAccount) {
      return this.contractAccountsCollection.contracts[
        selectedAccount.contractId
      ]
        .map(
          (legacyContractIdShadowed) =>
            this.contractAccountsCollection.accounts[legacyContractIdShadowed],
        )
        .filter(
          (account) =>
            account.status === ACCOUNT_STATUS_TYPES.ACTIVE ||
            account.status === ACCOUNT_STATUS_TYPES.ACTIVE_NOT_FUNDED,
        );
    }
    return [];
  }

  private generateAvailableAccounts() {
    const accountCollectionIsSetup$ = combineLatest(
      this.currentContract$.pipe(distinctUntilKeyChanged('contractID')),
      this.store.select('contractAccountsCollection').pipe(
        map((contractsComMap: any) => contractsComMap.toJS()),
        filter(
          (contractsCom: {
            loaded: boolean;
            loading: boolean;
            data: IContractAccountsCollection;
          }) => contractsCom.loaded,
        ),
        map((collection) => collection.data),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        tap((collection) => (this.contractAccountsCollection = collection)),
      ),
    ).pipe(
      tap(([{ contractID }]) => {
        this.canTransfer$.next(this.chekCanTransfer(contractID));
      }),
    );

    this.contractWithAccountsSubscription =
      accountCollectionIsSetup$.subscribe();

    this.transferAccounts$ = new Observable((observer) => {
      observer.next(this.getFilteredTransferAccounts());
    });

    /**
     * NOTE: One of these two merged observables is sending the last emitted value
     * every time one of them has a new value (shareReplay).
     * This should only happen to new subscribers, not for the ones already subscribed.
     * Right now this is not a problem, since the subscription to selectedLoremAccount$
     * only occurs in [selectedLoremAccount]="selectedLoremAccount$|async" and this behavior
     * won't happen there.
     * Beware of this if you need to use this.selectedLoremAccount$ inside this method.
     */
    this.selectedLoremAccount$ = this.currentContract$.pipe(
      merge(
        accountCollectionIsSetup$.pipe(
          map(
            () =>
              this.contractAccountsCollection.accounts[
                this.currentContract.contractID
              ],
          ),
        ),
      ),
    );
  }

  private scanForTradingUsaOperation() {
    combineLatest(
      this.store.select(selectFeatureFlag('tradingUsaOperation')),
      this.store.select(selectIsUsa),
    ).subscribe(([tradingUsaOperation, isUsaContract]) => {
      this.isUsaContract = isUsaContract;
      this.disabledTradingUsaOperation =
        this.isUsaContract && !tradingUsaOperation;
    });
  }

  /**
   * Checks if the current moment (as it comes from params.centralHour) is within operation time
   *
   * @param openTime in hours and minutes, assuming workingDay
   * @param closeTime in hours and minutes, assuming workingDay
   *
   * @returns whether now is between the opening and closing times
   */
  private isWithinCashOperationTime$(
    openTime: { hours: number; minutes: number },
    closeTime: { hours: number; minutes: number },
  ) {
    return this.store.select('params').pipe(
      takeUntil(this.destroyed$),
      map((value: any) => value.toJS()),
      filter((value: any) => !!value.centralHour),
      map((value) => value.centralHour),
      first(),
      map((centralHour: string) => {
        const now = moment(centralHour);

        const openingTimeMs = moment()
          .tz('America/Mexico_City')
          .set({ hour: openTime.hours, minute: openTime.minutes, second: 0 });

        const closingTimeMs = moment()
          .tz('America/Mexico_City')
          .set({ hour: closeTime.hours, minute: closeTime.minutes, second: 0 });

        return openingTimeMs < now && now < closingTimeMs;
      }),
    );
  }

  private initContractLayersObservable() {
    this.getContractCashLayers$ = this.currentContract$.pipe(
      switchMap(
        ({ loremContractId, loremAccountId, isLoremAccount, contractID }) =>
          isLoremAccount
            ? this.getLoremAccountCashLayers(loremContractId, loremAccountId)
            : this.getLegacyAccountCashLayers(contractID),
      ),
    );
  }

  private getLegacyAccountCashLayers(contractID: string) {
    return this.operationService.getWithdrawalAmount(contractID).pipe(
      map((withdrawalAmount: WithdrawalAmountResponse) =>
        this.generateCashLayers(
          withdrawalAmount.realWithdrawalAmount,
          0, // We use 0 because legacy accounts cannot transfer to other accounts
          withdrawalAmount.cashSameDay,
          withdrawalAmount.cash24Hrs,
          withdrawalAmount.cash48Hrs,
        ),
      ),
    );
  }

  private getLoremAccountCashLayers(
    loremContractId: string,
    loremAccountId: string,
  ) {
    return this.operationService
      .getAvailableTransferAmount(loremContractId, loremAccountId)
      .pipe(
        map((transfer: IAccountResponse) =>
          this.generateCashLayers(
            transfer.available_amount.amount,
            transfer.available_transfer_amount.amount,
            transfer.cash_today.amount,
            transfer.cash24_hrs.amount,
            transfer.cash48_hrs.amount,
          ),
        ),
      );
  }

  private generateCashLayers(
    availableToWithdraw: number,
    availableToTransfer: number,
    cashToday: number,
    cash24hrs: number,
    cash48hrs: number,
  ) {
    return {
      availableToWithdraw,
      availableToTransfer,
      cashList: [
        // Using Math.max(x, 0) to display only non-negative values
        {
          title: CASH_LAYERS.cashToday,
          cash: Math.max(cashToday, 0),
        },
        {
          title: CASH_LAYERS.cash24hrs,
          cash: Math.max(cash24hrs, 0),
        },
        {
          title: CASH_LAYERS.cash48hrs,
          cash: Math.max(cash48hrs, 0),
        },
      ],
    };
  }
}
